import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { Btn } from "../../components/Btn";
import { useToast } from "../../components/Toast";
import { useAuth } from "../../context/AuthContext";
import { Modal } from "../../components/Modal";
import { changePassword, getDevSettings, updateDevSettings } from "../../api/endpoints";
import { ApiError } from "../../api/types";

export default function DevSettingsPage() {
  const push = useToast();
  const { user } = useAuth();
  const [name, setName] = useState(user?.full_name ?? "");
  const [email, setEmail] = useState(user ? `${user.login}@corp.ru` : "");
  const [notifyPublish, setNotifyPublish] = useState(true);
  const [notifyRevoke, setNotifyRevoke] = useState(true);
  const [notifyStats, setNotifyStats] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const settingsQ = useQuery({ queryKey: ["dev", "settings"], queryFn: getDevSettings });
  useEffect(() => {
    if (!settingsQ.data) return;
    setName(settingsQ.data.full_name);
    setEmail(settingsQ.data.email);
    setNotifyPublish(settingsQ.data.notify_publish);
    setNotifyRevoke(settingsQ.data.notify_revoke);
    setNotifyStats(settingsQ.data.notify_stats);
  }, [settingsQ.data]);
  const saveMut = useMutation({
    mutationFn: () => updateDevSettings({
      full_name: name.trim(),
      email: email.trim(),
      notify_publish: notifyPublish,
      notify_revoke: notifyRevoke,
      notify_stats: notifyStats,
    }),
    onSuccess: () => push({ kind: "success", text: "Настройки профиля сохранены" }),
    onError: (error) => push({
      kind: "warn",
      text: error instanceof ApiError ? error.message : "Не удалось сохранить настройки",
    }),
  });

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Настройки профиля
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            Профиль разработчика и уведомления о событиях с приложениями
          </div>
        </div>
        <Btn
          icon={Save}
          variant="primary"
          disabled={!name.trim() || !email.trim() || saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? "Сохранение…" : "Сохранить"}
        </Btn>
      </header>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Профиль</h2>
          <div className="mt-4 space-y-3">
            <label className="block">
              <div className="text-[12px] font-medium text-[var(--color-fg)]">Имя</div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
              />
            </label>
            <label className="block">
              <div className="text-[12px] font-medium text-[var(--color-fg)]">Логин</div>
              <input
                value={user?.login ?? ""}
                disabled
                className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2 font-mono text-[13px] text-[var(--color-fg-muted)]"
              />
              <div className="mt-1 text-[11px] text-[var(--color-fg-muted)]">Изменяется только через администратора</div>
            </label>
            <label className="block">
              <div className="text-[12px] font-medium text-[var(--color-fg)]">Email для уведомлений</div>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
              />
            </label>
          </div>
        </section>

        <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Уведомления</h2>
          <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">События по вашим приложениям</p>
          <ul className="mt-4 divide-y divide-[var(--color-border)]">
            <NotifyRow label="Публикация версии"      hint="Когда вы или CI публикует новую версию"            value={notifyPublish}  onChange={setNotifyPublish} />
            <NotifyRow label="Отзыв версии"            hint="Когда администратор отзывает версию приложения" value={notifyRevoke}  onChange={setNotifyRevoke} />
            <NotifyRow label="Еженедельная статистика" hint="Сводка по установкам и скачиваниям"             value={notifyStats}    onChange={setNotifyStats} />
          </ul>
        </section>

        <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 lg:col-span-2">
          <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Безопасность</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <button
              onClick={() => setPasswordOpen(true)}
              className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-left transition-colors hover:bg-[var(--color-surface-2)]"
            >
              <div className="text-[13px] font-medium text-[var(--color-fg)]">Сменить пароль</div>
              <div className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">Последняя смена: 12.04.2026</div>
            </button>
          </div>
        </section>
      </div>
      <PasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </>
  );
}

function PasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const push = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const mutation = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      push({ kind: "success", text: "Пароль изменён" });
      setCurrent("");
      setNext("");
      setRepeat("");
      onClose();
    },
    onError: (error) => push({
      kind: "warn",
      text: error instanceof ApiError ? error.message : "Не удалось изменить пароль",
    }),
  });
  const valid = current.length > 0 && next.length >= 8 && next === repeat;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Сменить пароль"
      subtitle="Новый пароль должен содержать не менее 8 символов"
      size="sm"
      footer={
        <>
          <Btn onClick={onClose}>Отмена</Btn>
          <Btn variant="primary" disabled={!valid || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? "Сохранение…" : "Изменить пароль"}
          </Btn>
        </>
      }
    >
      <div className="space-y-3">
        <PasswordField label="Текущий пароль" value={current} onChange={setCurrent} />
        <PasswordField label="Новый пароль" value={next} onChange={setNext} />
        <PasswordField label="Повторите новый пароль" value={repeat} onChange={setRepeat} />
        {repeat && next !== repeat && (
          <p className="text-[12px] text-[var(--color-danger)]">Пароли не совпадают</p>
        )}
      </div>
    </Modal>
  );
}

function PasswordField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block text-[12px] text-[var(--color-fg)]">
      {label}
      <input
        type="password"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
      />
    </label>
  );
}

function NotifyRow({ label, hint, value, onChange }: { label: string; hint: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <li className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div>
        <div className="text-[13px] font-medium text-[var(--color-fg)]">{label}</div>
        <div className="text-[11px] text-[var(--color-fg-muted)]">{hint}</div>
      </div>
      <button
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={"relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors " + (value ? "bg-[var(--color-brand)]" : "bg-[var(--color-surface-3)]")}
      >
        <span className={"inline-block h-5 w-5 rounded-full bg-[var(--color-surface)] shadow transition-transform " + (value ? "translate-x-5" : "translate-x-0.5")} />
      </button>
    </li>
  );
}
